import {escapeHtml} from '../../core/escape-html.js';

/** @typedef {import('../../core/client/breadcrumb-overrides.core.js').BreadcrumbOverride} BreadcrumbOverride */

/**
 * Names the override a remove button removes: by its segment, else by its label, else by its
 * position in the list (for a row not filled in yet).
 *
 * @param {{segment: string, label: string}} override
 * @param {number} index position of the override in the list, from 0
 * @returns {string} accessible name of the override's remove button
 */
function breadcrumbOverrideRemoveLabel(override, index) {
    const name = override.segment.trim() || override.label.trim();
    return name ? `Remove breadcrumb override "${name}"` : `Remove breadcrumb override ${index + 1}`;
}

/**
 * @param {BreadcrumbOverride[]} overrides
 * @returns {string} one `.breadcrumb-override-row` per override
 */
export function breadcrumbOverridesMarkup(overrides) {
    return overrides.map((override, index) => `
            <div class="breadcrumb-override-row" data-id="${escapeHtml(override.id)}">
                <div class="breadcrumb-override-field">
                    <label for="breadcrumb-segment-${escapeHtml(override.id)}">Segment</label>
                    <input id="breadcrumb-segment-${escapeHtml(override.id)}" data-prop="segment" value="${escapeHtml(override.segment)}" placeholder="e.g. nieuwbouw" required />
                </div>
                <div class="breadcrumb-override-field">
                    <label for="breadcrumb-label-${escapeHtml(override.id)}">Label</label>
                    <input id="breadcrumb-label-${escapeHtml(override.id)}" data-prop="label" value="${escapeHtml(override.label)}" placeholder="e.g. Nieuwbouw" required />
                </div>
                <div class="breadcrumb-override-field">
                    <label for="breadcrumb-href-${escapeHtml(override.id)}">Href</label>
                    <input id="breadcrumb-href-${escapeHtml(override.id)}" data-prop="href" value="${escapeHtml(override.href)}" placeholder="/projecten#nieuwbouw-section" required />
                </div>
                <button type="button" class="btn btn-outline btn-sm btn-remove-breadcrumb-override" data-id="${escapeHtml(override.id)}"
                    data-index="${index}" aria-label="${escapeHtml(breadcrumbOverrideRemoveLabel(override, index))}">
                    Remove
                </button>
            </div>
        `).join('');
}

/**
 * @param {Iterable<HTMLElement>} rows the rendered `.breadcrumb-override-row` elements
 * @returns {BreadcrumbOverride[]} the overrides as currently typed
 */
export function readBreadcrumbOverrides(rows) {
    return Array.from(rows).map((row) => ({
        id: Number(row.dataset.id),
        segment: row.querySelector('[data-prop="segment"]')?.value || '',
        label: row.querySelector('[data-prop="label"]')?.value || '',
        href: row.querySelector('[data-prop="href"]')?.value || '',
    }));
}

/**
 * Renames the remove button of `row` after what its inputs now hold, so the name stays right
 * while the segment or label is typed.
 *
 * @param {HTMLElement} row a rendered `.breadcrumb-override-row`
 */
export function syncBreadcrumbOverrideRemoveLabel(row) {
    const button = row.querySelector('.btn-remove-breadcrumb-override');
    const [override] = readBreadcrumbOverrides([row]);
    button.setAttribute('aria-label', breadcrumbOverrideRemoveLabel(override, Number(button.dataset.index)));
}
